import { useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useAuth } from '@/api/auth'
import { useSyncStore } from '@/api/store'
import { useOverview } from '@/api/queries'
import type { Room, SearchHit } from '@/api/types'
import { Button, EmptyState, Spinner, TextField } from '@/components/ui'
import { RoomGlyph } from '@/components/RoomGlyph'
import { roomLabel, useRoomLabels } from '@/components/Sidebar'
import { useContainedScroll } from '@/hooks/useContainedScroll'
import { useLiveRefetch } from '@/hooks/useLiveRefetch'
import { formatBytes, formatDay, formatTime } from '@/lib/format'
import { AttachmentView } from '@/components/AttachmentView'
import { ActivityPage } from './ActivityPage'
import { SavedPage } from './SavedPage'
import styles from './OverviewPage.module.css'

const tabs = ['overview', 'activity', 'saved', 'files'] as const
type Tab = typeof tabs[number]
const title = (tab: Tab): string => tab.charAt(0).toUpperCase() + tab.slice(1)

function messagePath(entry: SearchHit): string {
  const params = new URLSearchParams({ message: entry.message.id })
  if (entry.message.parent_id) params.set('thread', entry.message.parent_id)
  return `/rooms/${encodeURIComponent(entry.room.id)}?${params}`
}

export function OverviewPage(): ReactElement {
  const { tab: parameter = 'overview' } = useParams()
  const { server } = useAuth()
  const tab = tabs.find((value) => value === parameter)
  if (!tab) return <Navigate to="/overview" replace />
  // Which section is showing is chosen from the sidebar, which lists all four;
  // a row of tabs here only repeated it.
  return <section className={styles.page} aria-labelledby="overview-title">
    <div className={styles.headerCard}>
      <header className={styles.header}>
        <div><h1 id="overview-title">{title(tab)}</h1><p>{tab === 'overview' ? `Your conversations in ${server?.display_name ?? 'your Space'}` : tab === 'activity' ? 'Unread messages, mentions and replies' : tab === 'saved' ? 'Messages you kept for later' : 'Files shared in your conversations'}</p></div>
        <span className={styles.space}>{server?.display_name ?? 'Your Space'}</span>
      </header>
    </div>
    <div className={styles.panel}>
      {tab === 'activity' ? <ActivityPage embedded /> : tab === 'saved' ?
        server?.capabilities?.saved_messages === false ? <EmptyState title="Saved messages are unavailable" description="Update this Space to enable saved messages." /> : <SavedPage embedded /> :
        <RecentFeed key={tab} files={tab === 'files'} />}
    </div>
  </section>
}

function RecentFeed({ files }: { files: boolean }): ReactElement {
  const { rooms: availableRooms } = useSyncStore()
  const rooms = availableRooms.filter((room) => room.my_membership != null)
  const labels = useRoomLabels()
  const [filter, setFilter] = useState('')
  const [fileLayout, setFileLayout] = useState<'cards' | 'table'>(() => {
    try { return localStorage.getItem('relaii:files-layout') === 'table' ? 'table' : 'cards' } catch { return 'cards' }
  })
  const toggleLayout = (): void => setFileLayout((previous) => {
    const next = previous === 'cards' ? 'table' : 'cards'
    try { localStorage.setItem('relaii:files-layout', next) } catch { /* Optional preference. */ }
    return next
  })
  const query = useOverview(files)
  const scroll = useRef<HTMLDivElement>(null)
  // A short list must not be draggable off the page on a touch screen.
  useContainedScroll(scroll)
  // A message landing in one of these rooms refreshes the feed, coalesced.
  useLiveRefetch(query.refetch, rooms.map((room) => room.id))
  const entries = (query.data ?? []).filter((entry) => rooms.some((room) => room.id === entry.room.id))
  const attachments = entries.flatMap((entry) => entry.message.attachments.filter((file) => file.status === 'ready').map((file) => ({ file, entry })))
    .filter(({ file }) => file.filename.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))
  return <div className={styles.scroll} ref={scroll}>
    <div className={styles.content}>
      {files ? <div className={styles.sectionHeading}><div><h2>Shared files</h2><p>Files from the conversations you belong to.</p></div>
        <div className={styles.fileActions}><Button variant="ghost" aria-label={fileLayout === 'cards' ? 'Show file table' : 'Show file cards'} onClick={toggleLayout}>{fileLayout === 'cards' ? '☷' : '▦'}</Button><Button variant="ghost" loading={query.isFetching && !query.isFetchingNextPage} onClick={() => void query.refetch()}>Refresh</Button></div></div> : null}
      {files ? <TextField label="Filter loaded files" type="search" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filename…" /> : null}
      {!files ? <ConversationOverview rooms={rooms} entries={entries} labels={labels} /> : query.isPending ? <div className={styles.empty}><Spinner label="Loading files" /></div> : query.isError ?
        <EmptyState title="Files could not be loaded" description={query.error.message} action={<Button onClick={() => void query.refetch()}>Try again</Button>} /> :
        attachments.length === 0 ? <div className={styles.empty}><h3>{filter ? 'No matching files' : 'No files yet'}</h3><p>Shared files appear here. Open a conversation to share one.</p></div> : <ul className={fileLayout === 'cards' ? styles.fileGrid : styles.fileTable}>{attachments.map(({ file, entry }) => <li key={file.id} className={styles.file}>
          {fileLayout === 'cards' ? <div className={styles.preview}>{file.preview_ready || file.mime_type?.startsWith('image/') ? <AttachmentView attachment={file} /> : <span className={styles.filePlaceholder} aria-hidden="true">{file.filename.includes('.') ? file.filename.split('.').pop()?.slice(0, 8).toUpperCase() : 'FILE'}</span>}</div> : <span className={styles.fileIcon} aria-hidden="true">↗</span>}
          <div className={styles.fileInfo}><a className={styles.filename} href={`${api.getBasePath()}/attachments/${encodeURIComponent(file.id)}/download`} target="_blank" rel="noopener noreferrer">{file.filename}</a>
            <p>{formatBytes(file.byte_size)} · {roomLabel(entry.room, labels)} · {formatDay(entry.message.created_at)}</p>
            <div className={styles.fileActions}><Link to={messagePath(entry)}>Open message</Link><a href={`${api.getBasePath()}/attachments/${encodeURIComponent(file.id)}/download`} target="_blank" rel="noopener noreferrer" aria-label={`Download ${file.filename}`}>↓ Download</a></div>
          </div>
        </li>)}</ul>}
      {query.hasNextPage ? <div className={styles.more}><Button loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>Load more {files ? 'files' : 'messages'}</Button></div> : null}
    </div>
  </div>
}

function ConversationOverview({ rooms, entries, labels }: { rooms: Room[]; entries: SearchHit[]; labels: ReadonlyMap<string, string> }): ReactElement {
  const recent = new Map<string, SearchHit>()
  for (const entry of entries) {
    const previous = recent.get(entry.room.id)
    if (!previous || previous.message.created_at < entry.message.created_at) recent.set(entry.room.id, entry)
  }
  const ordered = [...rooms].sort((a, b) =>
    Number(a.archived_at != null) - Number(b.archived_at != null) ||
    (recent.get(b.id)?.message.created_at ?? '').localeCompare(recent.get(a.id)?.message.created_at ?? '') || roomLabel(a, labels).localeCompare(roomLabel(b, labels)))
  const row = (room: Room): ReactElement => {
    const entry = recent.get(room.id)
    const label = roomLabel(room, labels)
    return <li key={room.id}><Link className={styles.conversationRow} to={entry ? messagePath(entry) : `/rooms/${encodeURIComponent(room.id)}`}>
      <RoomGlyph room={room} label={label} />
      <div className={styles.messageContent}><strong>{label}</strong><p>{room.archived_at ? 'Archived' : entry?.message.text || (entry?.message.attachments.length ? 'Shared a file' : 'Open conversation')}</p></div>
      {entry ? <time className={styles.rowTime} dateTime={entry.message.created_at}>{formatTime(entry.message.created_at)}<small>{formatDay(entry.message.created_at)}</small></time> : null}
    </Link></li>
  }
  const favorites = ordered.filter((room) => room.my_membership?.starred).slice(0, 4)
  return <>
    {favorites.length ? <section className={styles.conversationSection}><h2>Favorites</h2><ul className={styles.favorites}>{favorites.map(row)}</ul></section> : null}
    <div className={styles.columns}>
      {(['channel', 'direct'] as const).map((kind) => <section key={kind} className={styles.conversationSection}><h2>{kind === 'channel' ? 'Rooms' : 'Direct messages'}</h2>
        <ul className={styles.list}>{ordered.filter((room) => kind === 'channel' ? room.kind === 'channel' : room.kind !== 'channel').map(row)}</ul>
      </section>)}
    </div>
  </>
}
